import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import {
  PREFERRED_UNIT_SYSTEMS,
  type PreferredUnitSystem,
} from '@frontend/api';

import { UNITS } from 'constants/all';

import { displayOptions } from 'modules/common/viewer/dxfViewer/data/actions';
import {
  getDimensionalPrecision,
  getDisplayUnits,
} from 'modules/common/viewer/dxfViewer/data/reducer';
import {
  useViewerUnitSystemPreference,
  inferDisplayUnits,
} from 'modules/common/viewer/hooks/useViewerUnitSystemPreference';

import { useAppSelector } from 'store/hooks';

import { useUnitSystemContext } from 'utils/measurement';

const UNIT_SYSTEM_PREFERENCE_KEY = 'dxfViewer:unitSystemPreference';

export const useDisplayOptions = () => {
  const {
    preferredUnitSystem: groupPreferredUnitSystem,
    setPreferredUnitSystem: setGroupPreferredUnitSystem,
  } = useUnitSystemContext();
  const dimensionalPrecision = useAppSelector(getDimensionalPrecision);
  const displayUnits = useAppSelector(getDisplayUnits);
  const { setPreferredUnitSystem } = useViewerUnitSystemPreference({
    preferredUnitSystem: groupPreferredUnitSystem,
    setPreferredUnitSystem: setGroupPreferredUnitSystem,
    storageKey: UNIT_SYSTEM_PREFERENCE_KEY,
  });

  // The units the readouts format in, not the stored preference: until someone
  // picks a unit, those are the drawing's own.
  const unitSystem =
    displayUnits === UNITS.MM
      ? PREFERRED_UNIT_SYSTEMS.METRIC
      : PREFERRED_UNIT_SYSTEMS.IMPERIAL;

  const dispatch = useDispatch();
  const receiveDisplayUnits = useCallback(
    (units: { displayUnits: ReturnType<typeof inferDisplayUnits> }) => {
      dispatch(displayOptions.receiveDisplayUnits(units));
    },
    [dispatch],
  );
  const receiveDimensionalPrecision = useCallback(
    (precision: { dimensionalPrecision: number }) => {
      dispatch(displayOptions.receiveDimensionalPrecision(precision));
    },
    [dispatch],
  );

  const handleUnitsChange = (value: PreferredUnitSystem) => {
    if (value !== unitSystem) {
      receiveDisplayUnits({
        displayUnits: inferDisplayUnits(value),
      });
      setPreferredUnitSystem(value);
    }
  };

  const handlePrecisionChange = (valueAsString: string | number) => {
    const n =
      typeof valueAsString === 'number' ? valueAsString : Number(valueAsString);
    if (!Number.isFinite(n) || n === dimensionalPrecision) {
      return;
    }
    // The spec puts precision in a flat 0–6 range for both unit systems, so a
    // metric drawing is no longer capped at 4 the way the CAD viewer caps it.
    receiveDimensionalPrecision({
      dimensionalPrecision: Math.max(0, Math.min(6, Math.round(n))),
    });
  };

  return {
    unitSystem,
    dimensionalPrecision,
    handleUnitsChange,
    handlePrecisionChange,
  };
};
